import { useEffect, useState } from "react";
import { Eye, Calendar, Repeat, DollarSign, TrendingUp, Sparkles, Lock } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import ProInsightsCharts from "./ProInsightsCharts";

/**
 * ProviderAnalyticsCard — 30-day performance snapshot for the provider on
 * their own profile page. Owner-gated server-side, so we only mount this
 * when `isOwner && isProvider` from Profile.jsx.
 *
 * Shows: profile views, paid bookings, view→book conversion, unique
 * clients, repeat-client %, avg ticket $, total revenue $.
 */
export default function ProviderAnalyticsCard({ providerId }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!providerId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const { data } = await api.get(`/providers/${providerId}/analytics?days=${days}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || "Failed to load analytics");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [providerId, days]);

  return (
    <div className="parchment p-5 space-y-4" data-testid="provider-analytics-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="label-mystic text-primary">Performance</div>
          <h3 className="font-serif text-2xl mt-1">Last {days} days</h3>
        </div>
        <div className="flex gap-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1 text-[11px] uppercase tracking-wider rounded-sm border ${
                days === d
                  ? "border-primary text-primary bg-primary/10"
                  : "border-foreground/15 text-foreground/60 hover:text-foreground hover:border-foreground/40"
              }`}
              data-testid={`analytics-window-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-sm text-foreground/55">Loading…</div>}
      {err && <div className="text-sm text-destructive">{err}</div>}

      {!loading && !err && data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-5">
            <Metric icon={<Eye size={12} />} label="Profile views" value={data.views} />
            <Metric icon={<Calendar size={12} />} label="Paid bookings" value={data.bookings} accent />
            <Metric
              icon={<TrendingUp size={12} />}
              label="Conversion"
              value={`${data.conversionPct.toFixed(1)}%`}
            />
            <Metric label="Unique clients" value={data.uniqueClients} />
            <Metric
              icon={<Repeat size={12} />}
              label="Repeat clients"
              value={`${data.repeatClientPct.toFixed(1)}%`}
            />
            <Metric
              icon={<DollarSign size={12} />}
              label="Avg ticket"
              value={`$${(data.avgTicketUsd || 0).toFixed(2)}`}
            />
          </div>
          <div className="pt-3 border-t border-foreground/10 flex items-center justify-between">
            <span className="label-mystic text-foreground/55">Revenue · {days}d</span>
            <span className="font-serif text-2xl text-primary" data-testid="analytics-revenue">
              ${(data.totalRevenueUsd || 0).toFixed(2)}
            </span>
          </div>

          {/* ── Pro Insights upsell ─────────────────────────────────── */}
          {data.proInsightsActive ? (
            <>
              <div className="pt-3 border-t border-secondary/20 flex items-center gap-2"
                   data-testid="pro-insights-active">
                <Sparkles size={13} className="text-secondary" />
                <span className="label-mystic text-secondary">Pro Insights · Active</span>
                <span className="text-[11px] text-foreground/55 ml-auto">
                  Deeper charts unlocked
                </span>
              </div>
              <ProInsightsCharts providerId={providerId} days={days} />
            </>
          ) : (
            <ProInsightsUpsell price={data.proInsightsPriceUsd || 9.99} />
          )}
        </>
      )}
    </div>
  );
}

function Metric({ icon, label, value, accent = false }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-foreground/55 flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div className={`font-serif text-2xl mt-0.5 ${accent ? "text-primary" : "text-foreground"}`}>
        {value}
      </div>
    </div>
  );
}

function ProInsightsUpsell({ price }) {
  const startCheckout = async () => {
    try {
      const { data } = await api.post("/checkout/init", {
        origin: window.location.origin,
        kind: "pro_insights",
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout");
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start checkout");
    }
  };

  return (
    <div className="mt-3 p-4 rounded-sm border border-secondary/30 bg-secondary/[0.06] space-y-3"
         data-testid="pro-insights-upsell">
      <div className="flex items-start gap-2.5">
        <Sparkles size={16} className="text-secondary mt-0.5 shrink-0" strokeWidth={1.75} />
        <div className="flex-1">
          <div className="label-mystic text-secondary">Pro Insights · ${price.toFixed(2)}/mo</div>
          <p className="text-xs text-foreground/70 leading-relaxed mt-1">
            Unlock daily view trends, search keywords that surfaced your profile, and
            per-service conversion. Cancel anytime.
          </p>
          <ul className="text-[11px] text-foreground/60 mt-2 space-y-0.5 list-none">
            <li className="flex items-center gap-1.5"><Lock size={10} /> Daily views chart (12-month history)</li>
            <li className="flex items-center gap-1.5"><Lock size={10} /> Top search terms & referral sources</li>
            <li className="flex items-center gap-1.5"><Lock size={10} /> Per-service conversion funnel</li>
          </ul>
        </div>
      </div>
      <button
        type="button"
        onClick={startCheckout}
        className="w-full sm:w-auto px-5 py-2 rounded-sm bg-secondary text-secondary-foreground hover:bg-secondary/90 text-xs uppercase tracking-[0.22em] font-medium"
        data-testid="pro-insights-upgrade-btn"
      >
        Upgrade — ${price.toFixed(2)}/mo
      </button>
    </div>
  );
}
